import { cn } from '@repo/lib/cn';
import { ClockIcon, MapPinIcon, VideoIcon } from 'lucide-react';
import { useNavigate } from 'react-router';

import { Badge } from '@/components/ui/badge';
import { REPEAT_LABELS } from '@/utils/schedule';

import type { ScheduleTimetableEntry } from './ScheduleTimetable';

interface ScheduleMeetingCardProps {
  meeting: ScheduleTimetableEntry;
  showGroupName?: boolean;
}

export default function ScheduleMeetingCard(scheduleMeetingCardProps: ScheduleMeetingCardProps) {
  const { meeting, showGroupName = false } = scheduleMeetingCardProps;
  const { name, startT, endT, type, repeat, location, meetingURL, groupName, liveMeetingId } = meeting;
  const navigate = useNavigate();
  const isRemote = type === 'remote';
  const isLive = liveMeetingId !== undefined;

  return (
    <div
      onClick={isLive ? () => navigate(`/calendar/meeting/${liveMeetingId}`) : undefined}
      className={cn('border-border-primary bg-card flex flex-col gap-2 rounded-xl border p-3', isLive && 'border-border-success hover:bg-surface-success cursor-pointer transition-colors')}
    >
      <div className={'text-muted-foreground flex items-center gap-1.5'}>
        <ClockIcon className={'size-3.5 shrink-0'} />
        <span className={'text-foreground text-sm font-medium'}>
          {startT} – {endT}
        </span>
      </div>

      <p className={'text-foreground text-sm font-semibold'}>{name}</p>

      <div className={'flex flex-wrap gap-1'}>
        {isLive && (
          <Badge variant={'success'} border size={'sm'}>
            <span className={'relative flex size-2'}>
              <span className={'bg-fill-success absolute inline-flex h-full w-full animate-ping rounded-full opacity-75'} />
              <span className={'bg-fill-success relative inline-flex size-2 rounded-full'} />
            </span>
            Trwa
          </Badge>
        )}
        <Badge variant={'muted'} border size={'sm'}>
          {isRemote ? 'Zdalnie' : 'Stacjonarnie'}
        </Badge>
        {repeat && (
          <Badge variant={'muted'} border size={'sm'}>
            {REPEAT_LABELS[repeat]}
          </Badge>
        )}
        {showGroupName && groupName && (
          <Badge variant={'muted'} border size={'sm'}>
            {groupName}
          </Badge>
        )}
      </div>

      {isRemote
        ? meetingURL && (
            <a
              href={meetingURL}
              target={'_blank'}
              rel={'noreferrer'}
              onClick={(event) => event.stopPropagation()}
              className={'text-text-brand hover:text-text-brand-hover flex items-center gap-1.5 text-xs'}
            >
              <VideoIcon className={'size-3.5 shrink-0'} />
              <span className={'truncate'}>Dołącz do spotkania</span>
            </a>
          )
        : location && (
            <div className={'text-muted-foreground flex items-center gap-1.5 text-xs'}>
              <MapPinIcon className={'size-3.5 shrink-0'} />
              <span className={'truncate'}>{location.name}</span>
            </div>
          )}
    </div>
  );
}
